import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Bell, MessageSquare, Search, Send, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';
import { useAdminPush } from '@/hooks/useAdminPush';
import useAttentionSeen from '@/hooks/useAttentionSeen';
import NewSinceLoginPill from '@/components/admin/NewSinceLoginPill';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function dayKey(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function PushBanner({ phase, busy, onEnable }) {
  if (phase === 'loading' || phase === 'on' || phase === 'unsupported') return null;
  let copy = 'Notify me when a lead replies.';
  let action = 'Enable';
  if (phase === 'ios-install') {
    copy = 'Install DNA Admin to your Home Screen, then open it and allow notifications.';
    action = '';
  } else if (phase === 'denied') {
    copy = 'Notifications are blocked in this browser. Allow them for this site, then reload.';
    action = '';
  } else if (phase === 'unconfigured') {
    copy = 'Reply alerts are not configured on the server yet.';
    action = '';
  } else if (phase === 'error') {
    copy = 'Notifications could not be turned on. Try again.';
    action = 'Try again';
  }
  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-3 border border-white/10 bg-white/5 px-4 py-3"
      data-testid="outreach-push-banner"
    >
      <Bell className="w-4 h-4 text-blue-400 shrink-0" />
      <p className="text-sm font-['Manrope'] text-white/70 flex-1 min-w-[12rem]">{copy}</p>
      {action ? (
        <button
          type="button"
          onClick={onEnable}
          disabled={busy}
          className="min-h-11 px-4 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-['Manrope'] font-semibold"
        >
          {busy ? 'Turning on…' : action}
        </button>
      ) : null}
    </div>
  );
}

function StatusMark({ conversation }) {
  if (conversation.opted_out) {
    return <span className="text-[10px] font-['Manrope'] uppercase tracking-wide text-red-300 border border-red-400/40 px-1.5 py-0.5">STOP</span>;
  }
  if (conversation.automation_enabled) {
    return <span className="text-[10px] font-['Manrope'] uppercase tracking-wide text-emerald-300 border border-emerald-400/40 px-1.5 py-0.5">Auto</span>;
  }
  return <span className="text-[10px] font-['Manrope'] uppercase tracking-wide text-amber-300 border border-amber-400/40 px-1.5 py-0.5">Manual</span>;
}

export default function OutreachChannelInbox({ channel, threadId, onSelectThread }) {
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  useAttentionSeen(channel === 'sms' || channel === 'whatsapp' ? channel : '', token);
  const push = useAdminPush(token);
  const [conversations, setConversations] = useState([]);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);
  const [channelAuto, setChannelAuto] = useState(true);
  const [savingChannelAuto, setSavingChannelAuto] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const scrollerRef = useRef(null);
  const [pinChat, setPinChat] = useState(false);

  const fetchList = useCallback(async ({ silent } = {}) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/outreach/conversations`, {
        headers,
        params: { channel },
      });
      setConversations(res.data.conversations || []);
      setChannelAuto(res.data.automation_enabled !== false);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Failed to load conversations');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [token, headers, channel]);

  const fetchThread = useCallback(async ({ silent } = {}) => {
    if (!token || !threadId) {
      setThread(null);
      return;
    }
    if (!silent) setThreadLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/outreach/conversations/${threadId}`,
        { headers, params: { channel } },
      );
      setThread(res.data.conversation || null);
    } catch (err) {
      if (!silent) {
        toast.error(err.response?.data?.detail || 'Failed to load the conversation');
        setThread(null);
      }
    } finally {
      if (!silent) setThreadLoading(false);
    }
  }, [token, headers, channel, threadId]);

  useEffect(() => {
    setDraft('');
    setThread(null);
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  useEffect(() => {
    const listTimer = window.setInterval(() => {
      if (!document.hidden) fetchList({ silent: true });
    }, 12000);
    const threadTimer = window.setInterval(() => {
      if (!document.hidden && threadId) fetchThread({ silent: true });
    }, 5000);
    const onVisible = () => {
      if (document.hidden) return;
      fetchList({ silent: true });
      if (threadId) fetchThread({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(listTimer);
      window.clearInterval(threadTimer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchList, fetchThread, threadId]);

  useEffect(() => {
    const sync = () => {
      const narrow = window.matchMedia('(max-width: 1023px)').matches;
      const installed = window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
      setPinChat(narrow && installed);
    };
    sync();
    window.addEventListener('resize', sync);
    const mode = window.matchMedia('(display-mode: standalone)');
    mode.addEventListener?.('change', sync);
    return () => {
      window.removeEventListener('resize', sync);
      mode.removeEventListener?.('change', sync);
    };
  }, []);

  useEffect(() => {
    const narrowPwa = pinChat && Boolean(threadId);
    const viewport = window.visualViewport;
    if (!narrowPwa || !viewport) {
      document.documentElement.style.removeProperty('--outreach-keyboard');
      return undefined;
    }
    const syncKeyboard = () => {
      const overlap = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      document.documentElement.style.setProperty('--outreach-keyboard', `${Math.round(overlap)}px`);
    };
    syncKeyboard();
    viewport.addEventListener('resize', syncKeyboard);
    viewport.addEventListener('scroll', syncKeyboard);
    return () => {
      viewport.removeEventListener('resize', syncKeyboard);
      viewport.removeEventListener('scroll', syncKeyboard);
      document.documentElement.style.removeProperty('--outreach-keyboard');
    };
  }, [pinChat, threadId]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    scroller.scrollTop = scroller.scrollHeight;
  }, [thread?.messages?.length, threadId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((row) => (
      row.customer_name?.toLowerCase().includes(q)
      || row.customer_phone?.toLowerCase().includes(q)
      || row.vehicle_label?.toLowerCase().includes(q)
      || row.last_message_preview?.toLowerCase().includes(q)
    ));
  }, [conversations, search]);

  const messages = thread?.messages || [];
  const optedOut = Boolean(thread?.opted_out);
  const automationOn = thread ? thread.automation_enabled !== false : true;

  const channelLabel = channel === 'whatsapp' ? 'WhatsApp' : 'SMS';

  const toggleChannelAutomation = async () => {
    if (savingChannelAuto) return;
    const next = !channelAuto;
    setSavingChannelAuto(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/outreach/automation`,
        { channel, automation_enabled: next },
        { headers },
      );
      setChannelAuto(next);
      await fetchList({ silent: true });
      if (threadId) await fetchThread({ silent: true });
      toast.success(
        next
          ? `Automated ${channelLabel} replies are on for every lead`
          : `Automated ${channelLabel} replies are off for every lead`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update automated replies');
    } finally {
      setSavingChannelAuto(false);
    }
  };

  const deleteConversation = async (id) => {
    if (!id || deletingId) return;
    const other = channel === 'whatsapp' ? 'SMS' : 'WhatsApp';
    const confirmed = window.confirm(
      `Delete this ${channelLabel} conversation? Messages on ${channelLabel} are removed. ${other} messages for this number stay.`,
    );
    if (!confirmed) return;
    setDeletingId(id);
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/outreach/conversations/${id}`,
        { headers, params: { channel } },
      );
      if (id === threadId) onSelectThread('');
      await fetchList({ silent: true });
      toast.success(`${channelLabel} conversation deleted`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not delete the conversation');
    } finally {
      setDeletingId('');
    }
  };

  const toggleAutomation = async () => {
    if (!threadId || savingAuto || optedOut) return;
    const next = !automationOn;
    setSavingAuto(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/outreach/conversations/${threadId}`,
        { channel, automation_enabled: next },
        { headers },
      );
      await fetchThread({ silent: true });
      await fetchList({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update automated replies');
    } finally {
      setSavingAuto(false);
    }
  };

  const sendManual = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sending || !threadId) return;
    setSending(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/outreach/conversations/${threadId}/messages`,
        { channel, body },
        { headers },
      );
      setDraft('');
      await fetchThread({ silent: true });
      await fetchList({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'The message was not delivered');
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="outreach-channel-inbox">
      <PushBanner phase={push.phase} busy={push.busy} onEnable={push.enable} />
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={`Search ${channelLabel} by name, phone, vehicle, or message…`}
          className="w-full bg-white/5 border border-white/10 text-white pl-11 pr-4 py-2.5 font-['Manrope'] text-base lg:text-sm focus:outline-none focus:border-blue-500/50"
        />
      </div>
      <div
        className="mb-4 flex items-center justify-between gap-3 border border-white/10 bg-white/[0.03] px-4 py-3"
        data-testid="outreach-automation-all-bar"
      >
        <div className="min-w-0">
          <p className="text-sm font-['Manrope'] text-white">Auto reply for all {channelLabel}</p>
          <p className="text-xs font-['Manrope'] text-white/45">
            Turns automated replies on or off for every lead on {channelLabel}, including new ones.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={channelAuto}
          aria-label={`Auto reply for all ${channelLabel}`}
          data-testid="outreach-automation-all"
          disabled={savingChannelAuto}
          onClick={toggleChannelAutomation}
          className={`relative h-6 w-11 shrink-0 border disabled:opacity-40 ${
            channelAuto ? 'bg-emerald-500/30 border-emerald-400' : 'bg-white/10 border-white/20'
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 ${
              channelAuto ? 'left-6 bg-emerald-400' : 'left-0.5 bg-white/70'
            }`}
          />
        </button>
      </div>
      <div className="border border-white/10 bg-white/[0.03] grid lg:grid-cols-[340px_minmax(0,1fr)] min-h-[520px] lg:h-[calc(100dvh-220px)]">
        <div className={`${threadId ? 'hidden lg:flex' : 'flex'} flex-col min-h-0 border-white/10 lg:border-r`}>
          <div className="px-4 py-3 border-b border-white/10 text-xs font-['Manrope'] uppercase tracking-wide text-white/40">
            {channelLabel} conversations
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="outreach-conversation-list">
            {loading ? (
              <p className="text-white/40 font-['Manrope'] text-sm p-8 text-center">Loading conversations…</p>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center">
                <MessageSquare className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50 font-['Manrope'] text-sm">No {channelLabel} conversations yet</p>
                <p className="text-white/30 font-['Manrope'] text-xs mt-1">
                  A consented lead appears here after the first {channelLabel} message.
                </p>
              </div>
            ) : filtered.map((row) => {
              const selected = row.id === threadId;
              const title = row.customer_name || row.customer_phone || 'Customer';
              return (
                <div
                  key={row.id}
                  data-testid={`outreach-row-${row.id}`}
                  className={`flex items-stretch border-b border-white/10 border-l-2 ${
                    selected ? 'bg-white/10 border-l-blue-400' : 'border-l-transparent hover:bg-white/5'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onSelectThread(row.id)}
                    className={`relative flex-1 min-w-0 text-left px-4 py-3 ${row.missed_since_login ? 'pt-5' : ''}`}
                  >
                    {row.missed_since_login && (
                      <span className="absolute left-2 top-1.5">
                        <NewSinceLoginPill />
                      </span>
                    )}
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-['Manrope'] text-white font-medium truncate">{title}</p>
                      <StatusMark conversation={row} />
                    </div>
                    {row.customer_name && row.customer_phone ? (
                      <p className="text-xs font-['Manrope'] text-white/35 truncate">{row.customer_phone}</p>
                    ) : null}
                    <p className="text-xs font-['Manrope'] text-white/55 mt-1 truncate">
                      {row.last_message_preview || 'No message yet'}
                    </p>
                    <p className="text-[11px] font-['Manrope'] text-white/35 mt-1">
                      {formatDateTimeMdY(row.last_message_at, '')}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteConversation(row.id)}
                    disabled={deletingId === row.id}
                    data-testid={`outreach-delete-${row.id}`}
                    aria-label={`Delete ${title} ${channelLabel} conversation`}
                    className="min-h-11 min-w-11 shrink-0 inline-flex items-center justify-center text-white/35 hover:text-red-300 disabled:opacity-40"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div
          className={`${threadId ? 'flex' : 'hidden lg:flex'} ${pinChat && threadId ? 'outreach-pwa-thread' : ''} flex-col min-h-0 min-w-0`}
          data-testid="outreach-thread"
        >
          {!threadId ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <MessageSquare className="w-10 h-10 text-white/20 mb-3" />
              <p className="text-white/50 font-['Manrope'] text-sm">Select a conversation</p>
            </div>
          ) : threadLoading && !thread ? (
            <p className="text-white/40 font-['Manrope'] text-sm p-8 text-center">Loading conversation…</p>
          ) : !thread ? (
            <p className="text-white/40 font-['Manrope'] text-sm p-8 text-center">This conversation is not available.</p>
          ) : (
            <>
              <div className={`px-4 py-3 border-b border-white/10 flex items-start gap-3 ${pinChat ? 'outreach-pwa-bar' : ''}`}>
                <button
                  type="button"
                  onClick={() => onSelectThread('')}
                  className="lg:hidden min-h-11 min-w-11 inline-flex items-center justify-center text-white/70 hover:text-white"
                  data-testid="outreach-back"
                  aria-label="Back to conversations"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-['Manrope'] font-semibold truncate">
                    {thread.customer_name || thread.customer_phone || 'Customer'}
                  </p>
                  <p className="text-xs font-['Manrope'] text-white/45 truncate">
                    {[thread.lead_type_label, thread.customer_phone, thread.vehicle_label].filter(Boolean).join(' · ')}
                  </p>
                  {thread.lead_href ? (
                    <button
                      type="button"
                      onClick={() => navigate(thread.lead_href)}
                      className="text-xs font-['Manrope'] text-blue-400 hover:text-blue-300 mt-1"
                    >
                      Open lead
                    </button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => deleteConversation(thread.id)}
                    disabled={deletingId === thread.id}
                    data-testid="outreach-delete-thread"
                    aria-label={`Delete this ${channelLabel} conversation`}
                    className="min-h-11 min-w-11 inline-flex items-center justify-center text-white/50 hover:text-red-300 disabled:opacity-40"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <span className="text-xs font-['Manrope'] text-white/60 hidden sm:inline">Automated replies</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={automationOn}
                    aria-label="Automated replies"
                    data-testid="outreach-automation-switch"
                    disabled={savingAuto || optedOut}
                    onClick={toggleAutomation}
                    className={`relative h-6 w-11 border disabled:opacity-40 ${
                      automationOn ? 'bg-emerald-500/30 border-emerald-400' : 'bg-white/10 border-white/20'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 ${
                        automationOn ? 'left-6 bg-emerald-400' : 'left-0.5 bg-white/70'
                      }`}
                    />
                  </button>
                </div>
              </div>
              <div
                ref={scrollerRef}
                className={`flex-1 overflow-y-auto px-4 py-4 space-y-3 ${pinChat ? 'outreach-pwa-messages' : ''}`}
              >
                {messages.map((message, index) => {
                  const showDay = index === 0 || dayKey(message.created_at) !== dayKey(messages[index - 1].created_at);
                  const outbound = message.direction === 'outbound';
                  return (
                    <React.Fragment key={message.id || `${message.created_at}-${index}`}>
                      {showDay ? (
                        <p className="text-center text-[11px] font-['Manrope'] text-white/35 py-1">
                          {formatDateMdY(message.created_at, '')}
                        </p>
                      ) : null}
                      <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[75%] px-3 py-2 border ${
                            outbound
                              ? 'bg-blue-500/20 border-blue-500/30'
                              : 'bg-white/10 border-white/10'
                          }`}
                        >
                          <p className="text-sm font-['Manrope'] text-white whitespace-pre-wrap break-words">
                            {message.body}
                          </p>
                          <p className="text-[11px] font-['Manrope'] text-white/40 mt-1">
                            {formatDateTimeMdY(message.created_at, '')}
                          </p>
                          {message.status === 'failed' ? (
                            <p className="text-[11px] font-['Manrope'] text-red-300 mt-0.5">Not delivered</p>
                          ) : null}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
              {optedOut ? (
                <div className={`border-t border-white/10 px-4 py-3 text-sm font-['Manrope'] text-red-200 pb-[max(0.75rem,env(safe-area-inset-bottom))] ${pinChat ? 'outreach-pwa-dock' : ''}`}>
                  This customer texted STOP. Messaging is off until they reply START.
                </div>
              ) : automationOn ? null : (
                <form
                  onSubmit={sendManual}
                  className={`border-t border-white/10 p-3 flex gap-2 items-end pb-[max(0.75rem,env(safe-area-inset-bottom))] ${pinChat ? 'outreach-pwa-dock' : ''}`}
                  data-testid="outreach-composer"
                >
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder={`Message this lead on ${channelLabel}`}
                    className="flex-1 resize-none bg-white/5 border border-white/10 text-white px-3 py-2 font-['Manrope'] text-base lg:text-sm focus:outline-none focus:border-blue-500/50"
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    data-testid="outreach-send"
                    className="min-h-11 px-4 inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white text-sm font-['Manrope'] font-semibold"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
